/**
 * RequestFactory
 *
 * Purpose:
 *      Implements basic logic for request controllers to extend from. This is the basic model for interactions with
 *      the api.
 *
 * @author Humberto Barrera <humberto@inclancommunications.com>
 *
 * @date 4/06/2017 10:14 am
 */

import $ from 'jquery';
import * as Auth from './Auth.Service';
import {goTo} from '../GlobalTools';


export function Request()
{
	this._onError = undefined;
	this._onSuccess = undefined;
	this._onComplete = undefined;

	this._events = {};

	/**
	 * Publish
	 *
	 * Purpose:
	 *      Publishes(raises) an event within this class. This should only be called within this factory.
	 *
	 * Events:
	 *      See Subscribe function documentation.
	 *
	 * @param event (string) : The event to publish
	 * @param data (object) : The data that is to be returned, incapsulated in an object for a pass by reference
	 * effect.
	 *
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.Publish = function (event, data)
	{
		if (event in this._events)
		{
			for (var i in this._events[event])
			{
				this._events[event][i](data);
			}
		}
	}
	;

	/**
	 * ClearCallbacks
	 *
	 * Purpose:
	 *      Sets this._onSuccess and this._onError to undefined. Should be called before any new request is made so that
	 *      the this._onSuccess and this._onError callbacks are meant to be for the current request.
	 *
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.ClearCallbacks = function ()
	{
		this._onError = undefined;
		this._onSuccess = undefined;
		this._onComplete = undefined;
	}
	;

	this.ClearEvents = function ()
	{
		this._events = {};
	}
	;

	/**
	 * CallSuccess
	 *
	 * Purpose:
	 *      Used within Post/Get/Put/Delete calls to call the this._onSuccess callback. This should be used instead
	 *      of direct call to this._onSuccess because it triggers the subscribed events.
	 *
	 * @param data (mixed) : The data that was returned in the response.
	 *
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.CallSuccess = function (data)
	{
		try
		{
			if (this._onSuccess !== undefined)
			{
				this.Publish('on.calling.success', {data: data});
				this._onSuccess(data);
				this.Publish('on.called.success', {data: data});
			}
		} catch (e)
		{
		} finally
		{
			if (this._onComplete !== undefined)
			{
				this.Publish('on.calling.complete', {});
				this._onComplete();
				this.Publish('on.called.complete', {});
			}

			this.ClearEvents();
		}
	}
	;

	/**
	 * CallError
	 *
	 * Purpose:
	 *      Used within Post/Get/Put/Delete calls to call the this._onError callback. This should be used instead
	 *      of direct call to this._onError because it triggers the subscribed events.
	 *
	 * @param xhr (object) : The xhr object returned in response from server or the full request if partial error.
	 * @param status (string) : The status string from the server response or the error.type on partial error.
	 * @param err (obj) : The error object from the server response.
	 *
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.CallError = function (xhr, status, err)
	{
		try
		{
			if ('responseJSON' in xhr && (xhr.responseJSON.error.type === 'validation'))
			{
				var group = Auth.GetGroup();
				if (group === "4") {
					console.log('business')
				} else {
					goTo("/dashboard");
				}
				//Notify token has expired and redirect to login

			}
			if (this._onError !== undefined)
			{
				this.Publish('on.calling.error', {xhr: xhr, status: status, err: err});
				this._onError(xhr, status, err);
				this.Publish('on.called.error', {xhr: xhr, status: status, err: err});
			}
		} catch (e)
		{

		} finally
		{
			if (this._onComplete !== undefined)
			{
				this.Publish('on.calling.complete', {});
				this._onComplete();
				this.Publish('on.called.complete', {});
			}

			this.ClearEvents();
		}
	}
	;

	/**
	 * onSuccess
	 *
	 * Purpose:
	 *      Attaches an on success callback to the this._onSuccess variable. This will get called on a successful
	 *      response from the last request made to the api.
	 *
	 * @param fn (function) : The function you want called on a successful response.
	 *
	 * @return (object) : The this reference for chaining.
	 *
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.onSuccess = function (fn)
	{
		this._onSuccess = fn;
		return this;
	}
	;

	/**
	 * onError
	 *
	 * Purpose:
	 *      Attaches an on error callback to the this._onError variable. This will get called on an error
	 *      response from the last request made to the api.
	 *
	 * @param fn (function) : The function you want called on an error response.
	 *
	 * @return (object) : The this reference for chaining.
	 *
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.onError = function (fn)
	{
		this._onError = fn;
		return this;
	}
	;

	/**
	 * onComplete
	 *
	 * Purpose:
	 *      Called when the request is completed. Similar to finally in catch statement.
	 *
	 * @param fn (function) : The function you want called on an error response.
	 *
	 * @return (object) : The this reference for chaining.
	 *
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.onComplete = function (fn)
	{
		this._onComplete = fn;
		return this;
	}
	;

	/**
	 * Post
	 *
	 * Purpose:
	 *      A wrapper for the jquery $.post function which implements the callback system of this factory.
	 *
	 * @param url (string) : The url to make the post call to.
	 * @param data (mixed) : The data you want sent in the post call.
	 *
	 * @return (object) : The this reference for chaining.
	 *
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.Post = function (api_key=false, url, data, contentType = "application/json", credentials = false, token = false)
	{
		this.ClearCallbacks();
		$.ajax({
			url: url,
			data: data,
			contentType: contentType,
			xhrFields: {
				withCredentials: credentials
			},
			processData: (contentType === false) ? false : true,
			type: 'POST',
			beforeSend: function (xhr) {
				if(token){
					xhr.setRequestHeader("Authorization", token);
				}
				if(api_key){
					xhr.setRequestHeader("api_key", api_key);
				}

			}
		})
			.done(
				function (response)
				{
					if (!credentials) {
						if (!response.status)
						{
							this.CallError(response, response.error.type, response.error);
							this.ClearEvents();
							return;
						}
						this.CallSuccess(response.data);
					} else {
						if (response.code)
						{
							this.CallError(response, response.code, response.message);
							this.ClearEvents();
							return;
						}
						this.CallSuccess(response);
					}


				}.bind(this)
				)
			.fail(
				function (xhr, status, err)
				{
					this.CallError(xhr, status, err);
					this.ClearEvents();
				}.bind(this)
				);

		return this;
	}
	;

	/**
	 * Post V2
	 *
	 * Purpose:
	 *      A wrapper for the jquery $.get function which implements the callback system of this factory.
	 *
	 * @param url (string) : The url to make the post call to.
	 * @param data (mixed) : The data you want sent in the get call.
	 *
	 * @return (object) : Result.
	 * 
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.PostV2 = function (token, url, data, api_key=false)
	{
		this.ClearCallbacks();
		$.ajax({
			url: url,
			data: data,
			contentType: "application/x-www-form-urlencoded;",
			type: 'POST',
			beforeSend: function (xhr) {
				xhr.setRequestHeader("Authorization", token);
				if(api_key){
					xhr.setRequestHeader("api_key", api_key);
				}
			}
		})
			.done(
				function (response)
				{
					if (!response.status)
					{
						this.CallError(response, response.error.type, response.error);
						this.ClearEvents();
						return;
					}
					this.CallSuccess(response.data);
				}.bind(this)
				)
			.fail(
				function (xhr, status, err)
				{
					this.CallError(xhr, status, err);
					this.ClearEvents();
				}.bind(this)
				);

		return this;
	}
	;

	/**
	 * Get
	 *
	 * Purpose:
	 *      A wrapper for the jquery $.get function which implements the callback system of this factory.
	 *
	 * @param url (string) : The url to make the post call to.
	 * @param data (mixed) : The data you want sent in the get call.
	 *
	 * @return (object) : Result.
	 * 
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.Get = function (token, url, data, api_key=false)
	{
		this.ClearCallbacks();
		$.ajax({
			url: url,
			data: data,
			contentType: "application/json",
			dataType: "json",
			type: 'GET',
			beforeSend: function (xhr) {
				xhr.setRequestHeader("Authorization", token);
				if(api_key){
					xhr.setRequestHeader("api_key", api_key);
				}
			}
		})
			.done(
				function (response)
				{
					if (!response.status)
					{
						this.CallError(response, response.error.type, response.error);
						this.ClearEvents();
						return;
					}
					this.CallSuccess(response.data);
				}.bind(this)
				)
			.fail(
				function (xhr, status, err)
				{
					this.CallError(xhr, status, err);
					this.ClearEvents();
				}.bind(this)
				);

		return this;
	}
	;

	/**
	 * Put
	 *
	 * Purpose:
	 *      A wrapper for the jquery $.put function which implements the callback system of this factory.
	 *
	 * @param url (string) : The url to make the post call to.
	 * @param data (mixed) : The data you want sent in the get call.
	 *
	 * @return (object) : Result.
	 * 
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.Put = function (token, url, data)
	{
		this.ClearCallbacks();
		data = JSON.stringify(data);
		$.ajax({
			url: url,
			data: data,
			contentType: "application/json",
			dataType: "json",
			type: 'PUT',
			beforeSend: function (xhr) {
				xhr.setRequestHeader("Authorization", token);
			}
		})
			.done(
				function (response)
				{
					if (!response.status)
					{
						this.CallError(response, response.error.type, response.error);
						this.ClearEvents();
						return;
					}
					this.CallSuccess(response.data);
				}.bind(this)
				)
			.fail(
				function (xhr, status, err)
				{
					this.CallError(xhr, status, err);
					this.ClearEvents();
				}.bind(this)
				);

		return this;
	}
	;

	/**
	 * Delete
	 *
	 * Purpose:
	 *      A wrapper for the jquery $.put function which implements the callback system of this factory.
	 *
	 * @param url (string) : The url to make the post call to.
	 * @param data (mixed) : The data you want sent in the get call.
	 *
	 * @return (object) : Result.
	 * 
	 * @author Humberto Barrera <humberto@inclancommunications.com>
	 *
	 */
	this.Delete = function (token, url, data)
	{
		this.ClearCallbacks();
		$.ajax({
			url: url,
			data: data,
			contentType: "multipart/form-data",
			dataType: "json",
			type: 'DELETE',
			beforeSend: function (xhr) {
				xhr.setRequestHeader("Authorization", token);
			}
		})
			.done(
				function (response)
				{
					if (!response.status)
					{
						this.CallError(response, response.error.type, response.error);
						this.ClearEvents();
						return;
					}
					this.CallSuccess(response.data);
				}.bind(this)
				)
			.fail(
				function (xhr, status, err)
				{
					this.CallError(xhr, status, err);
					this.ClearEvents();
				}.bind(this)
				);

		return this;
	}
	;
}
 


// WEBPACK FOOTER //
// ./src/client/services/Request.Factory.js